import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex} from '@chakra-ui/react'
import {Component} from '@salesforce/commerce-sdk-react/components'

/**
 * PDCEditoStandardTilesGroup is a React functional component that renders a group of tiles
 * based on the provided product list, category, and regions. It utilizes a flexible grid layout
 * to display components dynamically.
 *
 * @param {Object} props - The properties object.
 * @param {Array} props.productList - An array of products to be associated with each component.
 * @param {Object} props.category - The category object to be associated with each component.
 * @param {Array} props.regions - An array of region objects, where each region contains components
 *                                to be displayed. Only the components from the first region are used.
 *
 * @returns {JSX.Element} A Flex container with a list of Box components, each containing a Component
 *                        element. The layout adapts to different screen sizes using responsive flex
 *                        properties and image sizes.
 */
export const PDCEditoStandardTilesGroup = ({productList, category, layoutIndex, regions}) => {
    let components = []
    if (regions) {
        components = regions[0].components || []
    }

    return (
        <Flex
            as="ul"
            listStyleType="none"
            rowGap={24}
            justifyContent={'center'}
            wrap={'wrap'}
            mx={[-5, null, -10, -5]}
        >
            {components.map((component, index) => {
                component.productList = productList
                component.category = category
                component.imageSizes = ['50vw', '50vw', '33vw', '25vw', '25vw', '20vw']
                component.layoutIndex = layoutIndex
                return (
                    <Box
                        as="li"
                        key={component?.id || index}
                        flex={['0 0 50%', '0 0 50%', '0 0 33%', '0 0 25%', '0 0 25%', '0 0 20%']}
                        maxWidth={['50%', '50%', '33%', '25%', '25%', '20%']}
                        px={5}
                    >
                        <Component component={component} />
                    </Box>
                )
            })}
        </Flex>
    )
}

PDCEditoStandardTilesGroup.propTypes = {
    productList: PropTypes.array,
    category: PropTypes.object,
    layoutIndex: PropTypes.number,
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    )
}
